import React, { useState } from "react";
import {
  IconTrash,
  IconPlus,
  IconChartBar,
  IconCalculator,
} from "@tabler/icons";
import {
  TextInput,
  Select,
  Button,
  Paper,
  Group,
  Stack,
  Divider,
  Box,
  Accordion,
  Text,
  ActionIcon,
  Textarea,
  Card,
} from "@mantine/core";
import _ from "lodash";

// Aggregator options
const AGGREGATORS = [
  { value: "count", label: "Count" },
  { value: "sum", label: "Sum" },
  { value: "avg", label: "Average" },
  { value: "min", label: "Minimum" },
  { value: "max", label: "Maximum" },
];

// Common fields for aggregation based on collection type
const AGGREGATION_FIELDS = {
  orders: [
    { value: "fyc", label: "First Year Commission" },
    { value: "premium", label: "Premium" },
    { value: "amount", label: "Amount" },
    { value: "netPremium", label: "Net Premium" },
    { value: "commission", label: "Commission" },
  ],
  clients: [
    { value: "totalPremium", label: "Total Premium" },
    { value: "totalCommission", label: "Total Commission" },
  ],
  agents: [
    { value: "totalSales", label: "Total Sales" },
    { value: "totalCommission", label: "Total Commission" },
  ],
  products: [
    { value: "salesCount", label: "Sales Count" },
    { value: "totalAmount", label: "Total Amount" },
  ],
};

const MetricForm = ({ metric, onUpdate, index, dataSources, allMetrics }) => {
  const updateMetricField = (field, value) => {
    const updatedMetric = { ...metric, [field]: value };
    onUpdate(index, updatedMetric);
  };

  const getSelectedDataSource = () => {
    return dataSources?.find((ds) => ds.name === metric.dataSource);
  };

  const getAvailableFields = () => {
    const selectedDS = getSelectedDataSource();
    if (!selectedDS) return [];
    return AGGREGATION_FIELDS[selectedDS.collectionName] || [];
  };

  const dataSourceOptions = [
    ...(dataSources?.map((ds) => ({
      value: ds.name,
      label: `${ds.name} (${ds.collectionName})`,
    })) || []),
    { value: "custom", label: "Custom Formula" },
  ];

  return (
    <Stack spacing="sm">
      <Group grow>
        <TextInput
          label="Metric Name"
          value={metric.name || ""}
          onChange={(e) => updateMetricField("name", e.target.value)}
          placeholder="e.g., totalPremium"
          required
          size="xs"
          autoComplete="off"
        />

        <Select
          label="Data Source"
          value={metric.dataSource || ""}
          onChange={(value) => updateMetricField("dataSource", value)}
          data={dataSourceOptions}
          placeholder="Select data source"
          required
          size="xs"
        />
      </Group>

      {metric.dataSource !== "custom" && (
        <Group grow>
          <Select
            label="Aggregator"
            value={metric.aggregator || ""}
            onChange={(value) => updateMetricField("aggregator", value)}
            data={AGGREGATORS}
            placeholder="Select aggregator"
            required
            size="xs"
          />

          <Select
            label="Field"
            value={metric.field || ""}
            onChange={(value) => updateMetricField("field", value)}
            data={getAvailableFields()}
            placeholder="Select field to aggregate"
            required
            disabled={!metric.dataSource}
            size="xs"
          />
        </Group>
      )}

      {metric.dataSource === "custom" && (
        <Stack spacing="xs">
          <Textarea
            label="Custom Formula"
            value={metric.formula || ""}
            onChange={(e) => updateMetricField("formula", e.target.value)}
            placeholder="e.g., totalPremium * 0.1 + bonusAmount"
            required
            minRows={3}
            description="Use other metric names to reference their values in calculations"
            size="xs"
          />
          {allMetrics && allMetrics.length > 0 && (
            <Box>
              <Text size="xs" color="dimmed" mb={2}>
                Available metric names to use in formula:
              </Text>
              <Text size="xs" color="blue">
                {allMetrics
                  .filter(
                    (m, i) => i !== index && m.name && m.name.trim() !== ""
                  )
                  .map((m) => m.name)
                  .join(", ") || "No other metrics defined yet"}
              </Text>
            </Box>
          )}
        </Stack>
      )}

      {metric.dataSource && metric.dataSource !== "custom" && (
        <Box>
          <Text size="xs" color="dimmed">
            Data Source: {getSelectedDataSource()?.name} (
            {getSelectedDataSource()?.collectionName})
          </Text>
          {getSelectedDataSource()?.filters &&
            Object.keys(getSelectedDataSource().filters).length > 0 && (
              <Text size="xs" color="blue">
                ✓ Filtered data source
              </Text>
            )}
        </Box>
      )}

      {metric.dataSource === "custom" && (
        <Box>
          <Text size="xs" color="dimmed">
            Custom calculated metric using formula
          </Text>
          <Text size="xs" color="orange">
            ⚠️ Ensure referenced metric names exist and are calculated before
            this metric
          </Text>
        </Box>
      )}
    </Stack>
  );
};

const CompensationMetrics = ({ form, name, ...props }) => {
  const metrics = _.get(form.values, name) ?? [];
  const dataSources = _.get(form.values, "datasource") ?? [];

  const onAddMetric = () => {
    const newMetric = {
      name: "",
      dataSource: "",
      aggregator: "",
      field: "",
      formula: "",
    };
    const updatedMetrics = [...metrics, newMetric];
    form.setFieldValue(name, updatedMetrics);
  };

  const onRemoveMetric = (index) => {
    const updatedMetrics = [...metrics];
    updatedMetrics.splice(index, 1);
    form.setFieldValue(name, updatedMetrics);
  };

  const updateMetric = (index, updatedMetric) => {
    const updatedMetrics = [...metrics];
    updatedMetrics[index] = updatedMetric;
    form.setFieldValue(name, updatedMetrics);
  };

  const getMetricSummary = (metric) => {
    if (metric.dataSource === "custom") {
      return metric.formula
        ? `FORMULA: ${metric.formula}`
        : "Custom formula (incomplete)";
    }

    if (!metric.aggregator || !metric.field) return "Incomplete metric";

    let summary = `${metric.aggregator.toUpperCase()}(${metric.field})`;
    if (metric.dataSource) {
      summary += ` FROM ${metric.dataSource}`;
    }
    return summary;
  };

  return (
    <>
      {dataSources.length === 0 && (
        <Card withBorder mb="md" p="md">
          <Text size="sm" color="orange">
            📋 Please define data sources first before creating metrics
          </Text>
        </Card>
      )}

      <Stack spacing="md" mb="xl">
        <Accordion multiple>
          {metrics.map((metric, index) => (
            <Accordion.Item key={index} value={`metric-${index}`} mb="md">
              <Accordion.Control>
                <Group>
                  <IconChartBar size={16} />
                  <Text weight={500}>
                    {metric.name || `Metric ${index + 1}`}
                  </Text>
                  <Text size="sm" color="dimmed">
                    {getMetricSummary(metric)}
                  </Text>
                  <div style={{ marginLeft: "auto" }}>
                    <ActionIcon
                      variant="filled"
                      //   color="red"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveMetric(index);
                      }}
                    >
                      <IconTrash size={13} />
                    </ActionIcon>
                  </div>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Paper withBorder p="sm">
                  <MetricForm
                    metric={metric}
                    onUpdate={updateMetric}
                    index={index}
                    dataSources={dataSources}
                    allMetrics={metrics}
                  />
                </Paper>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      </Stack>

      <Button
        size="xs"
        onClick={onAddMetric}
        leftIcon={<IconPlus size={16} />}
        disabled={dataSources.length === 0}
      >
        Add Metric
      </Button>

      {/* {metrics.length > 0 && (
        <Box
          mt="xl"
          p="md"
          style={{ backgroundColor: "#f8f9fa", borderRadius: 4 }}
        >
          <Text size="sm" weight={500} mb="xs">
            📊 Metrics Summary ({metrics.length} total)
          </Text>
          {metrics.map((metric, index) => (
            <Text key={index} size="xs" color="dimmed">
              • {metric.name || `Metric ${index + 1}`}:{" "}
              {getMetricSummary(metric)}
            </Text>
          ))}
        </Box>
      )} */}
    </>
  );
};

export default CompensationMetrics;
